/**
 * cartSlice — selected cart items for the active property bucket.
 *
 * v1 enforces a single-property cart (length === 1) in the UI, but the store
 * keeps items flat under the assumption that the property lives on
 * `propertySlice`. Multi-property buckets land in v2 (see master plan §4).
 */

import type { StateCreator } from 'zustand'

import type { CartItem, CartItemAiEditing, CartItemId } from 'models/purchaseFlowRevamp'
import type { PfStoreState } from '../store'

/** Generate a stable client-side cart-item id. Falls back to Math.random
 *  when crypto is unavailable (should never happen in browsers). */
const newCartItemId = (): CartItemId => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `ci-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Order-level instruction extras with their unit price, written natively by
 * the native Instructions step (`Instructions.page.tsx` → `setExtrasFromInstructions`,
 * derived from `instructionsSlice.extraServices` × the resolved instruction
 * catalogue; Phase E-A.7).
 *
 * Lives on `cartSlice` (rather than `instructionsSlice`) because the cart
 * total selector reads from this slice and we want a single source of truth
 * for "what shows up in the totals row of the sidebar". `setExtrasFromInstructions`
 * is the sole writer of the snapshots; `applyExtrasSessionPricing` only lays the
 * session's prices onto them.
 */
export interface CartExtraSnapshot {
  /** ProductKind of the extra (e.g. `FAST_DELIVERY`), or a synthetic kind for a
   *  BE-derived charge with no selectable product (`KEYS_PICKUP_TRAVEL_FEE`). */
  kind: string
  /** Display unit price as a string (decimal). Currency comes from any cart
   *  item's `priceSnapshot` — extras inherit the cart's currency in v1. */
  unitPrice: string
  /**
   * V-16 — namespaced i18n KEY (never a translated string) naming this charge in
   * the cart. Present ⇒ the cart itemises the snapshot as its own line instead
   * of only folding it into the total. Absent ⇒ today's total-only behaviour.
   */
  labelKey?: string
  /**
   * The chargeable product code behind this line, when there is one.
   *
   * Display needs none of this; the v2 PAYMENT INTENT does. Its amount is
   * `Σ(sent lines)` with no server-side fee derivation, so a quoted fee whose
   * code never reaches `buildPaymentIntentBody` is money Stripe does not
   * collect while the order still bills it. Carrying the code on the same
   * snapshot the total is built from is what keeps quote and charge in step.
   */
  productCode?: string
  /**
   * A further BE-derived charge that follows from choosing this one (the
   * key-pickup travel fee behind KEYPICKUP). Charged, never displayed: the wire
   * carries the code but not the amount, so it cannot be a cart line.
   */
  impliedFeeProductCode?: string
  /** Units of `productCode` the payment intent charges; 1 when absent. `unitPrice` is the line's total. */
  count?: number
  /**
   * What the session's price book charges for this line, when `…/sessions/{id}/pricing` has answered
   * (a OneSystem embed). Overrides `unitPrice` for every amount; written only by `applyExtrasSessionPricing`.
   */
  sessionPrice?: SessionPricedExtra
}

/** A fee line's charged total, and its public total to strike when higher (null otherwise). */
export interface SessionPricedExtra {
  amount: string
  baseAmount: string | null
}

export interface CartSliceState {
  items: CartItem[]
  /** Order-level instruction extras the user has selected on the native
   *  Instructions step. Written via `setExtrasFromInstructions`; their
   *  `sessionPrice` via `applyExtrasSessionPricing`. */
  extrasFromInstructions: CartExtraSnapshot[]
}

export interface CartSliceActions {
  /** Adds a new item. Returns the generated id. */
  addItem(item: Omit<CartItem, 'id'>): CartItemId
  /** Removes a cart item by id. No-op if not found. */
  removeItem(itemId: CartItemId): void
  /** Removes every line of one pack purchase: a pack's lines are removed together, never one by one. */
  removePack(selectionId: string): void
  /** Replaces an existing cart item in-place (same id) — used by the drawer
   *  when the user re-opens it and adjusts variant/options/extras. */
  replaceItem(itemId: CartItemId, next: Omit<CartItem, 'id'>): void
  /**
   * Sets one line's purchased quantity, clamped to at least 1.
   *
   * Narrow on purpose: the editing configuration page re-derives the quantity
   * from the number of source files handed over, and `replaceItem` would make it
   * rebuild — and risk rewriting — the whole line to change one number.
   */
  setItemQuantity(itemId: CartItemId, quantity: number): void
  /**
   * Sets one line's AI-editing settings.
   *
   * The settings are collected on the configuration page, after the line is in
   * the cart, so they are written here rather than carried in on the drawer's
   * commit. Same narrowness as [setItemQuantity]: `replaceItem` would rebuild the
   * whole line to change one field.
   */
  setItemAiEditing(itemId: CartItemId, aiEditing: CartItemAiEditing): void
  /**
   * Sets one line's client brief.
   *
   * Written on the configuration page, beside the visuals it describes, for every
   * product that has a page — see `productCommentSurface`. Blank clears it, so an
   * emptied box orders no comment rather than an empty one.
   */
  setItemNote(itemId: CartItemId, note: string): void
  /** Empties the cart. */
  clearCart(): void
  /**
   * Removes a set of cart items that are no longer available in the current
   * catalogue (stale items detected on draft resume). Also drops their entries
   * in `productConfigs`. Sets `draft.dirty = true` to trigger autosave so the
   * cleaned-up cart is persisted immediately.
   */
  clearStaleItems(itemIds: CartItemId[]): void
  /**
   * Phase E-A.7 — replaces the order-level instruction-extras snapshot. The
   * native Instructions step (`Instructions.page.tsx`) is the sole caller; pass
   * `[]` to clear. Intentionally does NOT flag `draft.dirty` because the
   * user-facing source of truth for extras is `instructionsSlice.extraServices`
   * (autosave-tracked separately).
   */
  setExtrasFromInstructions(extras: CartExtraSnapshot[]): void
  /**
   * Lays the session's prices onto the current extras, position for position (`null` leaves one as
   * quoted). A list that does not match the extras' length is ignored.
   */
  applyExtrasSessionPricing(prices: (SessionPricedExtra | null)[]): void
}

export type CartSlice = CartSliceState & CartSliceActions

export const createCartSlice: StateCreator<PfStoreState, [], [], CartSlice> = (set, get) => ({
  items: [],
  extrasFromInstructions: [],

  addItem: (item) => {
    const id = newCartItemId()
    set((state) => ({
      items: [...state.items, { ...item, id, quantity: Math.max(1, item.quantity) }],
      draft: { ...state.draft, dirty: true },
    }))
    return id
  },

  removeItem: (itemId) => {
    set((state) => {
      const next = state.items.filter((i) => i.id !== itemId)
      if (next.length === state.items.length) return state
      // productConfigs is keyed by `${cartItemId}|${flowId}` — remove all
      // entries whose key starts with `${itemId}|`.
      const configPrefix = `${itemId}|`
      const remainingConfigs: typeof state.productConfigs = {}
      for (const [k, v] of Object.entries(state.productConfigs)) {
        if (!k.startsWith(configPrefix)) remainingConfigs[k] = v
      }
      const { [itemId]: _removedConfirmed, ...remainingConfirmed } = state.cartItemUserConfirmed
      const { [itemId]: _removedAttachments, ...remainingAttachments } = state.attachments
      return {
        items: next,
        productConfigs: remainingConfigs,
        cartItemUserConfirmed: remainingConfirmed,
        attachments: remainingAttachments,
        draft: { ...state.draft, dirty: true },
      }
    })
  },

  removePack: (selectionId) => {
    get().items
      .filter((item) => item.pack?.selectionId === selectionId)
      .forEach((item) => get().removeItem(item.id))
  },

  replaceItem: (itemId, next) => {
    set((state) => {
      const idx = state.items.findIndex((i) => i.id === itemId)
      if (idx === -1) return state

      const previous = state.items[idx]
      const updated = [...state.items]
      updated[idx] = { ...next, id: itemId, quantity: Math.max(1, next.quantity) }

      // p3r1-03: if the shell product changed (different configurationFlowId),
      // the existing config is stale — clear it (and its confirmed flag) so
      // the shell step doesn't gate on an incompatible DTO.
      const shellChanged = previous.shellProductId !== next.shellProductId
      if (shellChanged) {
        const configPrefix = `${itemId}|`
        const hasConfigs = Object.keys(state.productConfigs).some((k) => k.startsWith(configPrefix))
        if (hasConfigs) {
          const remainingConfigs: typeof state.productConfigs = {}
          for (const [k, v] of Object.entries(state.productConfigs)) {
            if (!k.startsWith(configPrefix)) remainingConfigs[k] = v
          }
          const { [itemId]: _removedConfirmed, ...remainingConfirmed } = state.cartItemUserConfirmed
          return {
            items: updated,
            productConfigs: remainingConfigs,
            cartItemUserConfirmed: remainingConfirmed,
            draft: { ...state.draft, dirty: true },
          }
        }
      }

      return { items: updated, draft: { ...state.draft, dirty: true } }
    })
  },

  setItemQuantity: (itemId, quantity) => {
    set((state) => {
      const idx = state.items.findIndex((i) => i.id === itemId)
      if (idx === -1) return state
      const next = Math.max(1, Math.floor(quantity))
      if (state.items[idx].quantity === next) return state
      const updated = [...state.items]
      updated[idx] = { ...updated[idx], quantity: next }
      return { items: updated, draft: { ...state.draft, dirty: true } }
    })
  },

  setItemNote: (itemId, note) => {
    set((state) => {
      const idx = state.items.findIndex((i) => i.id === itemId)
      if (idx === -1) return state
      // Kept as typed: the box is bound to it, and trimming each keystroke would eat every space and new line.
      const next = note || undefined
      if (state.items[idx].note === next) return state
      const updated = [...state.items]
      updated[idx] = { ...updated[idx], note: next }
      return { items: updated, draft: { ...state.draft, dirty: true } }
    })
  },

  setItemAiEditing: (itemId, aiEditing) => {
    set((state) => {
      const idx = state.items.findIndex((i) => i.id === itemId)
      if (idx === -1) return state
      const current = state.items[idx].aiEditing
      if (current?.skyReplacement === aiEditing.skyReplacement && current?.cloudType === aiEditing.cloudType) {
        return state
      }
      const updated = [...state.items]
      updated[idx] = { ...updated[idx], aiEditing }
      return { items: updated, draft: { ...state.draft, dirty: true } }
    })
  },

  clearCart: () => {
    set((state) => ({
      items: [],
      productConfigs: {},
      cartItemUserConfirmed: {},
      attachments: {},
      extrasFromInstructions: [],
      draft: { ...state.draft, dirty: true },
    }))
  },

  setExtrasFromInstructions: (extras) => {
    set((state) => {
      // Reference-equality fast path keeps render churn down when the
      // bridge re-fires with an unchanged Set (same kinds, same prices).
      if (state.extrasFromInstructions.length === extras.length) {
        let same = true
        for (let i = 0; i < extras.length; i++) {
          if (
            state.extrasFromInstructions[i].kind !== extras[i].kind ||
            state.extrasFromInstructions[i].unitPrice !== extras[i].unitPrice ||
            state.extrasFromInstructions[i].labelKey !== extras[i].labelKey ||
            state.extrasFromInstructions[i].productCode !== extras[i].productCode ||
            state.extrasFromInstructions[i].impliedFeeProductCode !== extras[i].impliedFeeProductCode ||
            state.extrasFromInstructions[i].count !== extras[i].count
          ) {
            same = false
            break
          }
        }
        if (same) return state
      }
      return { extrasFromInstructions: extras }
    })
  },

  applyExtrasSessionPricing: (prices) => {
    set((state) => {
      const extras = state.extrasFromInstructions
      if (prices.length !== extras.length) return state
      const unchanged = extras.every((extra, i) =>
        (extra.sessionPrice?.amount ?? null) === (prices[i]?.amount ?? null) &&
        (extra.sessionPrice?.baseAmount ?? null) === (prices[i]?.baseAmount ?? null)
      )
      if (unchanged) return state
      return {
        extrasFromInstructions: extras.map((extra, i) => {
          const sessionPrice = prices[i]
          if (sessionPrice) return { ...extra, sessionPrice }
          const { sessionPrice: _dropped, ...quoted } = extra
          return quoted
        }),
      }
    })
  },

  clearStaleItems: (itemIds) => {
    if (itemIds.length === 0) return
    const idSet = new Set(itemIds)
    set((state) => {
      const nextItems = state.items.filter((i) => !idSet.has(i.id))
      if (nextItems.length === state.items.length) return state

      // productConfigs is keyed by `${cartItemId}|${flowId}` — remove all
      // entries whose key starts with any of the removed item ids.
      const nextConfigs: typeof state.productConfigs = {}
      for (const [k, v] of Object.entries(state.productConfigs)) {
        const belongs = Array.from(idSet).some((id) => k.startsWith(`${id}|`))
        if (!belongs) nextConfigs[k] = v
      }
      const nextConfirmed = { ...state.cartItemUserConfirmed }
      for (const id of itemIds) {
        delete nextConfirmed[id]
      }

      return {
        items: nextItems,
        productConfigs: nextConfigs,
        cartItemUserConfirmed: nextConfirmed,
        draft: { ...state.draft, dirty: true },
      }
    })
  },
})
